import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, CheckCircle } from 'lucide-react';

const ITChallenges = ({
    complexityTitle = "",
    complexitySubtitle = "",
    complexityPoints = [],
    complexityChallengesIntro = "",
    complexityChallenges = [],
    complexityConclusion = "",
    challengesTitle = "Technology Workforce Challenges",
    challengesList = [],
    solutionsTitle = "",
    solutionsIntro = "",
    solutionsPoints = [],
    solutionsDesignedForTitle = "",
    solutionsDesignedForList = []
}) => {
    return (
        <div className="py-24 bg-slate-50 space-y-24">
            
            {/* Section 1: Complexity */}
            {complexityTitle && (
                <section className="container mx-auto px-6 max-w-6xl">
                    <div className="bg-[#01014C] text-white p-12 md:p-16 rounded-[2.5rem] shadow-2xl relative overflow-hidden">
                        <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-500/10 blur-[100px] rounded-full" />
                        <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-cyan-500/10 blur-[100px] rounded-full" />
                        
                        <div className="relative z-10 max-w-4xl mx-auto">
                            <h2 className="text-3xl md:text-4xl font-bold syne leading-tight mb-8">
                                {complexityTitle}
                            </h2>
                            <p className="text-blue-100/70 text-lg md:text-lg leading-relaxed mb-12">
                                {complexitySubtitle}
                            </p>
                            
                            {/* Complexity Points Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
                                {complexityPoints.map((point, idx) => (
                                    <div key={idx} className="flex items-center gap-3 bg-white/5 border border-white/10 p-4 rounded-xl">
                                        <div className="w-2 h-2 rounded-full bg-blue-500" />
                                        <span className="text-sm font-semibold text-blue-50">{point}</span>
                                    </div>
                                ))}
                            </div>
                            
                            <hr className="border-white/10 my-10" />
                            
                            {complexityChallengesIntro && (
                                <div className="space-y-6">
                                    <p className="text-blue-400 font-mono text-sm tracking-wider uppercase flex items-center gap-2">
                                        <AlertCircle size={16} /> {complexityChallengesIntro}
                                    </p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {complexityChallenges.map((challenge, idx) => (
                                            <div key={idx} className="flex items-start gap-3 text-slate-300">
                                                <span className="text-blue-400 font-bold">•</span>
                                                <span className="text-sm">{challenge}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {complexityConclusion && (
                                <p className="mt-12 text-slate-300/80 text-sm italic border-l-2 border-blue-500 pl-4 leading-relaxed">
                                    {complexityConclusion}
                                </p>
                            )}
                        </div>
                    </div>
                </section>
            )}

            {/* Section 2: Workforce Challenges Cards Grid */}
            {challengesList && challengesList.length > 0 && (
                <section className="container mx-auto px-6">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-4xl md:text-3xl font-bold text-slate-900 syne leading-tight mb-4">
                            {challengesTitle}
                        </h2>
                        <p className="text-slate-500 text-sm md:text-base leading-relaxed">
                            Common hiring bottlenecks, technical talent shortages, and retention hurdles impacting modern enterprise teams.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
                        {challengesList.map((item, idx) => (
                            <motion.div 
                                whileHover={{ y: -5 }} 
                                key={idx} 
                                className="p-10 bg-white rounded-[2rem] border border-slate-100 shadow-xl shadow-slate-200/50 flex flex-col justify-between"
                            >
                                <div>
                                    <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center text-white mb-6 font-bold text-lg">
                                        {idx + 1}
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-800 mb-4 font-sans leading-snug">
                                        {item.title}
                                    </h3>
                                    <p className="text-slate-500 font-semibold text-sm mb-6 leading-relaxed">
                                        {item.description}
                                    </p>
                                    
                                    {item.subpoints && item.subpoints.length > 0 && (
                                        <ul className="space-y-3 pl-4 mb-6">
                                            {item.subpoints.map((sub, sIdx) => (
                                                <li key={sIdx} className="list-disc text-sm text-slate-600 leading-relaxed">
                                                    {sub}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                
                                {item.conclusion && (
                                    <p className="text-xs font-bold text-blue-600 border-t border-slate-50 pt-4 mt-2">
                                        {item.conclusion}
                                    </p>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </section>
            )}

            {/* Section 3: Specialized Solutions */}
            {solutionsTitle && (
                <section className="container mx-auto px-6 max-w-6xl">
                    <div className="bg-white p-12 md:p-16 rounded-[2.5rem] border border-slate-100 shadow-xl shadow-slate-200/50">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl md:text-3xl font-bold text-slate-900 syne leading-tight mb-8">
                                {solutionsTitle}
                            </h2>
                            <p className="text-slate-600 text-md leading-relaxed mb-12">
                                {solutionsIntro}
                            </p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
                                {solutionsPoints.map((sol, idx) => (
                                    <div key={idx} className="flex items-center gap-3 bg-slate-50 border border-slate-100 p-4 rounded-2xl hover:bg-blue-50 transition-colors">
                                        <CheckCircle size={18} className="text-blue-600 shrink-0" />
                                        <span className="text-sm font-bold text-slate-700">{sol}</span>
                                    </div>
                                ))}
                            </div>
                            
                            {solutionsDesignedForTitle && (
                                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 space-y-6">
                                    <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">
                                        {solutionsDesignedForTitle}
                                    </p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {solutionsDesignedForList.map((target, idx) => (
                                            <div key={idx} className="flex items-center gap-2 text-slate-600">
                                                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
                                                <span className="text-sm font-medium">{target}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            )}

        </div>
    );
};

export default ITChallenges;